import { useSelector } from "react-redux";
import PieChart from "common/Charts/PieChart/PieChart";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { RootState } from "app/store.ts";
import { PrecisionConfig } from "types";
import { LanguageNameSpaces, PrecisionType } from "@shared/types";
import { useBottomNavigator } from "utils/hooks/misc";
import React, { CSSProperties, useMemo } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface CoinsPieChartProps {
  total: number;
  pieChartData: { value: any; color: string; name: string }[];
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
  loadingSelector?: (state: RootState) => { loading: boolean; loaded: boolean };
  hideTitle?: boolean;
  tooltipElementId?: string;
  chartParentElementId?: string;
  title?: string;
  extraInfoStyle?: CSSProperties;
}

const CoinsPieChart = ({
  total,
  pieChartData,
  hideTitle = false,
  loadingSelector,
  precisionSelector,
  tooltipElementId,
  chartParentElementId,
  title,
  extraInfoStyle,
}: CoinsPieChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const defaultLoading = useMemo(() => ({ loading: false, loaded: false }), []);
  const { loading } = useSelector(loadingSelector ?? (() => defaultLoading));
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const totalText = useMemo(
    () => (
      <TextWithPrecisionAndIcon
        precisionSelector={precisionSelector}
        precisionType={PrecisionType.AMOUNT}
        text={total}
        round={true}
      />
    ),
    [total, precisionSelector]
  );

  return (
    <PieChart
      data={pieChartData}
      loading={loading && (!isBottomNavigator || !pieChartData.length)}
      chartName={!hideTitle ? title ?? t("assets.assets_allocation") : ""}
      total={totalText}
      chartParentElementId={chartParentElementId}
      tooltipElementId={tooltipElementId}
      isMobileMainPageChart={isBottomNavigator}
      extraInfoStyle={extraInfoStyle}
    />
  );
};

export default React.memo(CoinsPieChart);
